import { useCallback, useEffect, useState } from 'react';

import {
  listVenuesForReview, setVenueApproval, setVenueRevenueShare, startVenueOnboarding,
  refreshVenueOnboarding, type AdminVenue,
} from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatDateShort } from '../../lib/format';
import './AdminPages.css';

/**
 * Venue approval queue. The counterpart to AdminArtists, and deliberately the
 * same shape so an admin works both the same way.
 *
 * What approval actually controls, which the copy states rather than implies:
 * an unapproved venue is absent from the organizer directory, 404s on a detail
 * read by anyone but its owner, and cannot receive NEW booking requests.
 *
 * Two things this screen adds over the artist queue, because venues differ:
 *
 *   COMPLETENESS. A venue profile can be a name and nothing else. Approving an
 *   empty shell puts a room in front of organizers with no capacity, no specs
 *   and no price, so the missing pieces are listed before the decision.
 *
 *   BOOKING EXPOSURE. Revoking does not void bookings already made — that is a
 *   commitment between organizer and venue, not something a listing flag should
 *   silently undo. The count is shown so the admin knows what a revocation
 *   leaves standing rather than assuming it cleans up after itself.
 *
 * THE REVENUE SHARE LIVES HERE AND NOWHERE ELSE. It is a negotiated commercial
 * term between Voleto and the venue, so it is set from the admin console rather
 * than the venue portal: a venue that could set its own rate could award itself
 * a cut of every show it hosts. The engine enforces that too, the column is
 * absent from the venue profile's editable whitelist.
 *
 * THE CONTROL IS PROSPECTIVE AND SAYS SO. Changing a rate governs events
 * published from now on. Events already on sale settle at the rate frozen onto
 * them when they were published, which is why the confirmation names how many
 * are unaffected rather than leaving an admin to assume it applies everywhere.
 */

const CHECKS: { key: keyof AdminVenue['completeness']; label: string }[] = [
  { key: 'hasCapacity', label: 'Capacity' },
  { key: 'hasAddress', label: 'Address' },
  { key: 'hasTechnicalSpecs', label: 'Technical specs' },
  { key: 'hasPricing', label: 'Pricing' },
  { key: 'hasImages', label: 'Images' },
];

export function AdminVenues() {
  const [tab, setTab] = useState<'false' | 'true' | 'all'>('false');
  const [venues, setVenues] = useState<AdminVenue[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listVenuesForReview(tab)
      .then(setVenues)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not load venues.');
        setVenues([]);
      })
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  async function decide(venue: AdminVenue, approved: boolean) {
    if (!approved && !window.confirm(
      `Revoke approval for ${venue.venueName}?\n\n`
      + 'It disappears from the organizer directory and cannot receive new booking '
      + 'requests until approved again.\n\n'
      + (venue.bookings > 0
        ? `${venue.bookings} existing booking${venue.bookings === 1 ? '' : 's'} `
          + `(${venue.confirmedBookings} confirmed) are NOT cancelled by this, `
          + 'the organizer and venue would need to settle those between themselves.'
        : 'It has no bookings, so nothing is left standing.'),
    )) return;

    setBusyId(venue.venueId);
    setError(null);
    try {
      const result = await setVenueApproval(venue.venueId, approved);
      setVenues((cur) =>
        cur?.map((v) =>
          v.venueId === venue.venueId ? { ...v, approved: result.approved } : v) ?? cur);
      // A decided venue leaves the pending tab.
      if (tab !== 'all') {
        setVenues((cur) => cur?.filter((v) => v.venueId !== venue.venueId) ?? cur);
      }
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not update that venue.');
    } finally {
      setBusyId(null);
    }
  }

  /**
   * Set or clear the negotiated revenue share.
   *
   * Confirmed before it is sent, and the confirmation states the one thing an admin is
   * most likely to assume wrongly: this does not re-price anything already on sale.
   */
  async function saveRevenueShare(venue: AdminVenue) {
    const typed = window.prompt(
      `Revenue share for ${venue.venueName}\n\n`
      + 'Percentage of GROSS ticket revenue for shows at this venue, deducted at '
      + 'settlement alongside card processing and the platform fee.\n\n'
      + 'This applies to events published FROM NOW ON. Events already published keep the '
      + 'rate frozen onto them when they went on sale and are not affected.\n\n'
      + 'Leave blank for no revenue share.',
      venue.revenueSharePercentage != null ? String(venue.revenueSharePercentage) : '',
    );
    if (typed === null) return;

    const trimmed = typed.trim();
    const rate = trimmed === '' ? null : Number(trimmed);
    if (rate !== null && (!Number.isFinite(rate) || rate < 0 || rate > 100)) {
      setError('The revenue share must be a percentage between 0 and 100, or blank for none.');
      return;
    }

    setBusyId(venue.venueId);
    setError(null);
    try {
      const result = await setVenueRevenueShare(venue.venueId, rate);
      setVenues((cur) => cur?.map((v) => (v.venueId === venue.venueId
        ? { ...v, revenueSharePercentage: result.revenueSharePercentage } : v)) ?? cur);
      if (result.eventsAtFrozenRate > 0) {
        window.alert(
          `Saved. ${result.eventsAtFrozenRate} event`
          + `${result.eventsAtFrozenRate === 1 ? '' : 's'} already published at this venue `
          + 'keep the rate they were sold under and are unchanged.');
      }
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not set that revenue share.');
    } finally {
      setBusyId(null);
    }
  }

  /** Open hosted Stripe onboarding so the venue can actually be paid. */
  async function onboard(venue: AdminVenue) {
    setBusyId(venue.venueId);
    setError(null);
    try {
      const { url } = await startVenueOnboarding(venue.venueId, window.location.origin);
      window.location.assign(url);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not start onboarding.');
      setBusyId(null);
    }
  }

  /**
   * Our copy of the account state is only as fresh as the last read.
   *
   * `payout` is read with `?.` throughout this screen. The engine always sends it, but the
   * portal and the engine deploy separately and the migration that adds these columns is
   * run by hand, so there is a window where a newer portal talks to an engine that has not
   * got them yet. Optional chaining makes that window render "no arrangement" instead of
   * throwing and blanking the whole venue queue.
   */
  async function refreshPayout(venue: AdminVenue) {
    setBusyId(venue.venueId);
    setError(null);
    try {
      const payout = await refreshVenueOnboarding(venue.venueId);
      setVenues((cur) => cur?.map((v) => (v.venueId === venue.venueId
        ? { ...v, payout: { ...v.payout, ...payout } } : v)) ?? cur);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not refresh that account.');
    } finally {
      setBusyId(null);
    }
  }

  const num = (v: number | null) => (v == null ? ' | ' : v.toLocaleString('en-US'));

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Venue approval</h1>
          <p className="adm-lede">
            Unapproved venues are hidden from the organizer directory and cannot receive
            booking requests.
          </p>
        </div>
        {venues && tab === 'false' && (
          <div className="adm-stat">
            <span className="adm-stat__value mono">{venues.length}</span>
            <span className="adm-stat__label">awaiting review</span>
          </div>
        )}
      </header>

      <div className="adm-filters">
        {([['false', 'Pending'], ['true', 'Approved'], ['all', 'All']] as const).map(([v, label]) => (
          <button key={v} type="button"
            className={`adm-tab${tab === v ? ' is-active' : ''}`}
            onClick={() => setTab(v)}>
            {label}
          </button>
        ))}
      </div>

      {error && <p className="adm-status adm-error" role="alert">{error}</p>}
      {loading && <p className="adm-status">Loading…</p>}
      {!loading && venues?.length === 0 && (
        <p className="adm-status adm-muted">
          {tab === 'false' ? 'Nothing awaiting review.' : 'No venues here.'}
        </p>
      )}

      <div className="adm-list">
        {venues?.map((v) => {
          const missing = CHECKS.filter((c) => !v.completeness[c.key]);
          return (
            <article key={v.venueId} className="adm-card">
              <header className="adm-card__head">
                <div className="adm-artist">
                  <span className="adm-artist__avatar" aria-hidden="true">
                    {v.images[0]
                      ? <img src={v.images[0]} alt="" />
                      : v.venueName.slice(0, 1)}
                  </span>
                  <div>
                    <h2 className="adm-card__title">{v.venueName}</h2>
                    <p className="adm-card__meta">
                      {[v.city, v.state].filter(Boolean).join(', ') || 'No location'}
                      {v.capacity != null ? ` · capacity ${num(v.capacity)}` : ' · capacity not stated'}
                      {' · '}created {formatDateShort(v.createdAt)}
                    </p>
                    {v.account && (
                      <p className="adm-card__meta adm-muted">
                        {v.account.name || '-'} <span className="mono">{v.account.email}</span>
                        {!v.account.verified && (
                          <span className="adm-pill adm-pill--pending">Email unverified</span>
                        )}
                      </p>
                    )}
                  </div>
                </div>

                <div className="adm-card__status">
                  <span className={`adm-pill adm-pill--${v.approved ? 'approved' : 'pending'}`}>
                    {v.approved ? 'Approved' : 'Pending'}
                  </span>
                  <span className="adm-hint mono">
                    {v.bookings} booking{v.bookings === 1 ? '' : 's'}
                    {v.confirmedBookings > 0 ? ` · ${v.confirmedBookings} confirmed` : ''}
                  </span>
                </div>
              </header>

              <div className="adm-card__body">
                {missing.length > 0 && (
                  <p className="adm-flash" role="status">
                    <strong className="adm-warn">Incomplete profile.</strong>{' '}
                    Missing {missing.map((c) => c.label.toLowerCase()).join(', ')}. Approving
                    puts this in front of organizers as-is.
                  </p>
                )}

                <dl className="adm-metrics">
                  {v.address && <div><dt>Address</dt><dd>{v.address}</dd></div>}
                  {v.layoutOptions && <div><dt>Layouts</dt><dd>{v.layoutOptions}</dd></div>}
                  {v.technicalSpecs && <div><dt>Technical</dt><dd>{v.technicalSpecs}</dd></div>}
                  {v.pricingStructure && <div><dt>Pricing</dt><dd>{v.pricingStructure}</dd></div>}
                  {v.requirements && <div><dt>Requirements</dt><dd>{v.requirements}</dd></div>}
                </dl>

                {/* The negotiated cut, and whether it could actually be paid. Shown on
                    every venue rather than only those with a rate, because "none" is a
                    decision an admin needs to be able to see and change. */}
                <dl className="adm-metrics">
                  <div>
                    <dt>Revenue share</dt>
                    <dd>
                      {v.revenueSharePercentage != null
                        ? <span className="mono">{v.revenueSharePercentage}% of gross</span>
                        : <span className="adm-muted">None</span>}
                    </dd>
                  </div>
                  {v.revenueSharePercentage != null && (
                    <div>
                      <dt>Payout account</dt>
                      <dd>
                        {v.payout?.isMock
                          ? <span className="adm-pill adm-pill--pending">Mock, cannot be paid</span>
                          : v.payout?.canReceiveTransfers
                            ? <span className="adm-pill adm-pill--approved">Ready</span>
                            : (
                              <span className="adm-pill adm-pill--pending">
                                {v.payout?.onboardingStatus === 'not_started'
                                  ? 'Onboarding not started'
                                  : v.payout?.stripeDisabledReason || v.payout?.onboardingStatus}
                              </span>
                            )}
                      </dd>
                    </div>
                  )}
                </dl>

                {v.revenueSharePercentage != null && !v.payout?.canReceiveTransfers && (
                  <p className="adm-flash" role="status">
                    <strong className="adm-warn">This venue cannot be paid yet.</strong>{' '}
                    Its share is still deducted at settlement and recorded as owed, but the
                    transfer defers until Stripe onboarding is complete.
                  </p>
                )}

                <div className="adm-card__actions">
                  <button type="button" className="button button--ghost adm-btn-sm"
                    disabled={busyId === v.venueId}
                    onClick={() => saveRevenueShare(v)}>
                    {v.revenueSharePercentage != null ? 'Edit revenue share' : 'Set revenue share'}
                  </button>
                  {v.revenueSharePercentage != null && !v.payout?.stripeConnectAccountId && (
                    <button type="button" className="button button--ghost adm-btn-sm"
                      disabled={busyId === v.venueId}
                      onClick={() => onboard(v)}>
                      Start payout onboarding
                    </button>
                  )}
                  {v.payout?.stripeConnectAccountId && !v.payout.canReceiveTransfers && (
                    <button type="button" className="button button--ghost adm-btn-sm"
                      disabled={busyId === v.venueId}
                      onClick={() => refreshPayout(v)}>
                      {busyId === v.venueId ? 'Checking…' : 'Refresh payout status'}
                    </button>
                  )}
                  {!v.approved ? (
                    <button type="button" className="button button--primary adm-btn-sm"
                      disabled={busyId === v.venueId}
                      onClick={() => decide(v, true)}>
                      {busyId === v.venueId ? 'Approving…' : 'Approve venue'}
                    </button>
                  ) : (
                    <button type="button" className="button button--ghost adm-btn-sm sup-danger"
                      disabled={busyId === v.venueId}
                      onClick={() => decide(v, false)}>
                      {busyId === v.venueId ? 'Revoking…' : 'Revoke approval'}
                    </button>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
